import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { interventionsClient } from 'api';
import type {
  CancelInterventionRequest,
  FinishInterventionRequest,
  Intervention,
  OrderCustomFormToAdd,
  OrderCustomFormToEdit,
  PlanInterventionRequest,
  ReplanInterventionRequest,
  SendDepartureLateNotificationRequest,
  StartDepartureForInterventionRequest
} from 'api/models/mainservice';
import type { ApiError, CustomFormReportDocument, GeoCoordinate, TableResult } from 'common/models';
import { handleApiErrorWithNotification } from 'helpers/errorHandling';
import { getTableQueryKey } from 'helpers/tableHelpers';
import type { OrderDetailAlt } from 'pages/orders/models';
import {
  getCustomFormMaterialQueryKeyBuilder,
  getOrderDetailQueryKeyBuilderSimple,
  getOrderDetailQueryKeyBuilderV2,
  getOrdersQueryKeyBuilder
} from 'pages/orders/queries';

import type { InterventionDetailAlt, InterventionTableQuery, InterventionToEdit } from './models';

export const getInterventionsQueryKeyBuilder = () => 'interventions';
export const getInterventionDetailQueryKeyBuilder = (id: string) => ['intervention', id];
export const getInterventionCustomFormReportKeyBuilder = (
  interventionId: string,
  customFormId: string,
  reportId: string,
  serviceObjectId?: string
) =>
  serviceObjectId
    ? ['interventionReport', interventionId, customFormId, reportId, serviceObjectId]
    : ['interventionReport', interventionId, customFormId, reportId];

export const useGetInterventionsQuery = (params: InterventionTableQuery) => {
  return useQuery<TableResult<Intervention>, ApiError>(
    getTableQueryKey(getInterventionsQueryKeyBuilder(), params),
    () => interventionsClient.getInterventions(params),
    {
      keepPreviousData: true
    }
  );
};

export const useGetInterventionDetailQuery = (orderId: string, interventionId: string) => {
  return useQuery<InterventionDetailAlt, ApiError>(
    getInterventionDetailQueryKeyBuilder(interventionId),
    () => interventionsClient.getIntervention(orderId, interventionId)
  );
};

export const useGetInterventionCustomFormReport = (
  orderId: string,
  interventionId: string,
  customFormId: string,
  reportId: string,
  serviceObjectId?: string
) => {
  return useQuery<CustomFormReportDocument, ApiError>(
    getInterventionCustomFormReportKeyBuilder(
      interventionId,
      customFormId,
      reportId,
      serviceObjectId
    ),
    () =>
      interventionsClient.getReport(
        orderId,
        interventionId,
        customFormId,
        reportId,
        serviceObjectId
      ),
    {
      refetchOnWindowFocus: false
    }
  );
};

export const useConfirmInterventionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<InterventionDetailAlt, ApiError, { orderId: string; interventionId: string }>(
    interventionsClient.confirmIntervention,
    {
      onSuccess: async (data, { interventionId, orderId }) => {
        await queryClient.invalidateQueries(getOrdersQueryKeyBuilder());
        await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderV2(orderId));
        await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderSimple(orderId));
        queryClient.setQueryData<InterventionDetailAlt>(
          getInterventionDetailQueryKeyBuilder(interventionId),
          data
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

export const useStartDepartureInterventionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    InterventionDetailAlt,
    ApiError,
    {
      orderId: string;
      interventionId: string;
      values: StartDepartureForInterventionRequest;
    }
  >(interventionsClient.startDepartureIntervention, {
    onSuccess: async (data, { orderId, interventionId }) => {
      await queryClient.invalidateQueries(getOrdersQueryKeyBuilder());
      await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderV2(orderId));
      await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderSimple(orderId));
      queryClient.setQueryData<InterventionDetailAlt>(
        getInterventionDetailQueryKeyBuilder(interventionId),
        data
      );
    }
    // onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useSendDepartureLateInterventionMutation = () => {
  return useMutation<
    undefined,
    ApiError,
    {
      orderId: string;
      interventionId: string;
      values: SendDepartureLateNotificationRequest;
    }
  >(
    interventionsClient.sendDepartureLateIntervention
    //   {
    //   onError: (error) => handleApiErrorWithNotification(error)
    // }
  );
};

export const useStartInterventionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    InterventionDetailAlt,
    ApiError,
    { orderId: string; interventionId: string; location?: GeoCoordinate }
  >(interventionsClient.startIntervention, {
    onSuccess: async (data, { orderId, interventionId }) => {
      await queryClient.invalidateQueries(getOrdersQueryKeyBuilder());
      await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderV2(orderId));
      await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderSimple(orderId));
      queryClient.setQueryData<InterventionDetailAlt>(
        getInterventionDetailQueryKeyBuilder(interventionId),
        data
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useFinishInterventionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    InterventionDetailAlt,
    ApiError,
    {
      orderId: string;
      interventionId: string;
      data: FinishInterventionRequest;
    }
  >(interventionsClient.finishIntervention, {
    onSuccess: (data, { interventionId }) => {
      queryClient.setQueryData<InterventionDetailAlt>(
        getInterventionDetailQueryKeyBuilder(interventionId),
        data
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useConfirmSignaturesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    InterventionDetailAlt,
    ApiError,
    { orderId: string; interventionId: string; location?: GeoCoordinate }
  >(interventionsClient.confirmSignatures, {
    onSuccess: (data, { interventionId }) => {
      queryClient.setQueryData<InterventionDetailAlt>(
        getInterventionDetailQueryKeyBuilder(interventionId),
        data
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useCancelInterventionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    InterventionDetailAlt,
    ApiError,
    {
      orderId: string;
      interventionId: string;
      data: CancelInterventionRequest;
    }
  >(interventionsClient.cancelIntervention, {
    onSuccess: async (data, { orderId, interventionId }) => {
      await queryClient.invalidateQueries(getOrdersQueryKeyBuilder());
      await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderV2(orderId));
      await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderSimple(orderId));
      queryClient.setQueryData<InterventionDetailAlt>(
        getInterventionDetailQueryKeyBuilder(interventionId),
        data
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const usePlanInterventionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    OrderDetailAlt,
    ApiError,
    { orderId: string; intervention: PlanInterventionRequest }
  >(interventionsClient.planIntervention, {
    onSuccess: async (data, { orderId }) => {
      await queryClient.invalidateQueries(getOrdersQueryKeyBuilder());
      await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderSimple(orderId));
      queryClient.setQueryData<OrderDetailAlt>(getOrderDetailQueryKeyBuilderV2(orderId), data);
    }
    // onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useReplanInterventionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    InterventionDetailAlt,
    ApiError,
    {
      orderId: string;
      interventionId: string;
      intervention: ReplanInterventionRequest;
    }
  >(interventionsClient.replanIntervention, {
    onSuccess: async (data, { orderId, interventionId }) => {
      await queryClient.invalidateQueries(getOrdersQueryKeyBuilder());
      await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderV2(orderId));
      await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderSimple(orderId));
      queryClient.setQueryData<InterventionDetailAlt>(
        getInterventionDetailQueryKeyBuilder(interventionId),
        data
      );
    }
    // onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useEditInterventionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    InterventionDetailAlt,
    ApiError,
    {
      orderId: string;
      interventionId: string;
      values: InterventionToEdit;
    }
  >(interventionsClient.editIntervention, {
    onSuccess: async (data, { orderId, interventionId }) => {
      await queryClient.invalidateQueries(getOrdersQueryKeyBuilder());
      await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderV2(orderId));
      await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderSimple(orderId));
      queryClient.setQueryData<InterventionDetailAlt>(
        getInterventionDetailQueryKeyBuilder(interventionId),
        data
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useAddInterventionCustomFormMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    InterventionDetailAlt,
    ApiError,
    { orderId: string; interventionId: string; data: OrderCustomFormToAdd }
  >(interventionsClient.addCustomForm, {
    onSuccess: async (data, { interventionId, orderId }) => {
      await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderSimple(orderId));
      await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderV2(orderId));
      queryClient.setQueryData<InterventionDetailAlt>(
        getInterventionDetailQueryKeyBuilder(interventionId),
        data
      );
    }
  });
};

export const useEditInterventionCustomFormMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    InterventionDetailAlt,
    ApiError,
    {
      orderId: string;
      interventionId: string;
      customFormId: string;
      data: OrderCustomFormToEdit;
    }
  >(interventionsClient.editCustomForm, {
    onSuccess: async (data, { interventionId, orderId }) => {
      await queryClient.removeQueries(getCustomFormMaterialQueryKeyBuilder());
      await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderSimple(orderId));
      await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderV2(orderId));
      queryClient.setQueryData<InterventionDetailAlt>(
        getInterventionDetailQueryKeyBuilder(interventionId),
        data
      );
    }
  });
};

export const useDeleteInterventionCustomFormMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    InterventionDetailAlt,
    ApiError,
    {
      orderId: string;
      interventionId: string;
      customFormId: string;
    }
  >(interventionsClient.deleteCustomForm, {
    onSuccess: async (data, { interventionId, orderId }) => {
      await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderSimple(orderId));
      queryClient.setQueryData<InterventionDetailAlt>(
        getInterventionDetailQueryKeyBuilder(interventionId),
        data
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useSaveInterventionCustomFormMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    InterventionDetailAlt,
    ApiError,
    {
      orderId: string;
      interventionId: string;
      customFormId: string;
      data: string;
    }
  >(interventionsClient.saveCustomFormProgress, {
    onSuccess: async (data, { interventionId, orderId, customFormId }) => {
      await queryClient.invalidateQueries(
        getOrderDetailQueryKeyBuilderSimple(orderId, interventionId, customFormId)
      );
      await queryClient.setQueryData<InterventionDetailAlt>(
        getInterventionDetailQueryKeyBuilder(interventionId),
        data
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useSignInterventionReportMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    InterventionDetailAlt,
    ApiError,
    {
      orderId: string;
      customFormId: string;
      interventionId: string;
      reportId: string;
      data: FormData;
      serviceObjectId?: string;
    }
  >(interventionsClient.signInReport, {
    onSuccess: async (
      data,
      { interventionId, orderId, customFormId, serviceObjectId, reportId }
    ) => {
      await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderSimple(orderId));
      await queryClient.invalidateQueries(
        getInterventionCustomFormReportKeyBuilder(
          interventionId,
          customFormId,
          reportId,
          serviceObjectId
        )
      );
      queryClient.setQueryData<InterventionDetailAlt>(
        getInterventionDetailQueryKeyBuilder(interventionId),
        data
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useResetStateInterventionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<InterventionDetailAlt, ApiError, { orderId: string; interventionId: string }>(
    interventionsClient.resetIntervention,
    {
      onSuccess: async (data, { orderId, interventionId }) => {
        await queryClient.invalidateQueries(getOrdersQueryKeyBuilder());
        await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderSimple(orderId));
        await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderV2(orderId));
        queryClient.setQueryData<InterventionDetailAlt>(
          getInterventionDetailQueryKeyBuilder(interventionId),
          data
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

export const useDeleteInterventionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { orderId: string; interventionId: string }>(
    interventionsClient.deleteIntervention,
    {
      onSuccess: async (data, { orderId }) => {
        await queryClient.invalidateQueries(getOrdersQueryKeyBuilder());
        await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderSimple(orderId));
        await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderV2(orderId));
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

export const useRegenerateInterventionCustomFormMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    InterventionDetailAlt,
    ApiError,
    { orderId: string; interventionId: string; customFormId: string }
  >(interventionsClient.regenerateInterventionCustomForm, {
    onSuccess: async (data, { orderId, interventionId }) => {
      queryClient.setQueryData<InterventionDetailAlt>(
        getInterventionDetailQueryKeyBuilder(interventionId),
        data
      );

      await queryClient.invalidateQueries(getOrdersQueryKeyBuilder());
      await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderSimple(orderId));
      await queryClient.invalidateQueries(getOrderDetailQueryKeyBuilderV2(orderId));
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};
